import type { Content } from '@/modules/Entertainment/interface/Content';
import type { Session } from 'next-auth';
import { useSession } from 'next-auth/react';
import {
  type QueryKey,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from '@/libs/axios/api';
import { apiConfig } from '@/config/api';
import { checkIsTokenValid } from '@/utils/jwt';

const changeBookmark = async (
  session: Session | null,
  { id, object, bookmarked }: Payload
) => {
  try {
    if (!bookmarked) {
      await api.post(apiConfig.routes.bookmark.save, {
        type: object,
        content_id: id,
      });
      return;
    }

    await api.delete(apiConfig.routes.bookmark.delete(id, object));
  } catch {
    if (!session || !checkIsTokenValid(session.access_token)) {
      window.dispatchEvent(new Event('open-unauthenticated-modal'));
    }

    throw new Error('Error on bookmark');
  }
};

export const useBookmark = (queryKeyToInvalidate?: QueryKey) => {
  const queryClient = useQueryClient();
  const { data: session } = useSession();

  return useMutation({
    mutationFn: (payload: Payload) => changeBookmark(session, payload),
    onSettled: () =>
      queryKeyToInvalidate &&
      queryClient.invalidateQueries({ queryKey: queryKeyToInvalidate }),
  });
};

type Payload = Pick<Content, 'id' | 'object' | 'bookmarked'>;
